import {
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Button,
  Menu,
  Box,
  Typography,
  Checkbox,
  FormControl,
  FormGroup as MuiFormGroup,
  Badge,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import PeopleIcon from '@mui/icons-material/People';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { statisticsApi, organizationsApi } from "api";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ProcessesList from "./ProcessesList";
import { StartedProcess } from "./ProcessItem";
import { useForm, useTranslations } from "hooks";
import { getTranslation } from "common";
import FilterListIcon from "@mui/icons-material/FilterList";
import SortIcon from "@mui/icons-material/Sort";
import { Organization } from "model/organizationModel";
import ProcessModelFilter from "./ProccessModelFilter/ProcessModelFilter";
import { ProcessModelType } from "model/processModel";
import {
  SortCriterion,
  checkUserDataSortType,
  customSort,
  getLabelDataFromJsonTags,
  getUserData,
  hasDuplicates,
} from "common/utilities";
import { SortState } from "hooks/useSortableList";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import CheckIcon from "@mui/icons-material/Check";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { User } from "./UserItem";
import { Group } from "./GroupsPanel";
import { Role } from "./RolesPanel";
import { Robot } from "hooks/useRobots";
import FlowentiButton from "./flowenti-ui-kit/FlowentiButton";
import MonitorSearchBar from "./MonitorSearchBar";
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { AccountCircle, LabelOutlined } from "@mui/icons-material";
import Labels from "./labels/Labels";
import { LabelData } from "./labels/label_Interface";

interface Data<T> {
  loaded: boolean;
  data: T[];
}
interface PageData {
  users: Data<User>;
  robots: Data<Robot>;
  groups: Data<Group>;
  processes: Data<StartedProcess>;
  roles: Data<Role>;
  processModels: Data<ProcessModelType>;
  userDataKeys: Data<string>;
  tags: Data<LabelData>;
  priorities: Data<string>;
  allGroups: Data<string>;
  allRoles: Data<string>;
}
interface ProcessStartedProps {
  organization: Organization;
}
const getUserDataKeysFromProcesses = (processes: StartedProcess[]) => {
  const keys = new Set<string>();
  processes.forEach((process) => {
    try {
      const userData = getUserData(process.process_userdata);
      Object.keys(userData).forEach((key) => keys.add(key));
    } catch (e) { }
  });
  return Array.from(keys);
};

const getUniqueValues = (processes: StartedProcess[], field: 'jsontag' | 'jsongroups' | 'jsonroles' | 'priorityname') => {
  const values = new Set<string>();
  processes.forEach((process) => {
    const rawValue = process[field];
    if (rawValue) {
      if (field === 'priorityname') {
        values.add(rawValue);
      } else {
        try {
          // Se è già un oggetto (axios lo parsa automaticamente se è JSON)
          const parsed = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
          if (Array.isArray(parsed)) {
            parsed.forEach((item: any) => {
              const name = item.tag || item.groupname || item.rolename;
              if (name) values.add(name);
            });
          }
        } catch (e) {
          console.error(`Error parsing ${field}:`, e, rawValue);
        }
      }
    }
  });
  const result = Array.from(values);
  if (field === 'jsontag') console.log("Extracted unique tags:", result);
  return result;
};

const getUniqueTags = (processes: StartedProcess[]): LabelData[] => {
  const tagsMap = new Map<string, LabelData>();
  processes.forEach((process) => {
    const rawValue = process.jsontag;
    if (rawValue) {
      try {
        const parsed = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
        if (Array.isArray(parsed)) {
          parsed.forEach((item: any) => {
            if (item.tag) {
              tagsMap.set(item.tag, {
                id: item.idtag,
                name: item.tag,
                css: item.color,
              });
            }
          });
        }
      } catch (e) {
        console.error(`Error parsing jsontag:`, e, rawValue);
      }
    }
  });
  return Array.from(tagsMap.values());
};

const LOCAL_STORAGE_KEY = `processesFilters`;
const SORT_STORAGE_KEY = `processesSorts`;
const ProcessStarted: React.FC<ProcessStartedProps> = ({ organization }) => {
  const translations = useTranslations();
  const [showAllGroups, setShowAllGroups] = useState(false);
  const [toDo, setToDo] = useState<boolean>(true);
  const [doing, setDoing] = useState<boolean>(true);
  const { values, changeHandler, setValues } = useForm(
    {
      status: "current",
      modelids: [],
      activityids: [],
      userids: [],
      groupids: [],
      roleids: [],
      search: "",
      userDataKeys: [],
      tags: [],
      groups: [],
      roles: [],
      priorities: [],
    },
    []
  );

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [sortAnchorEl, setSortAnchorEl] = useState<HTMLElement | null>(null);
  const [userDataMenuAnchorEl, setUserDataMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [tagsMenuAnchorEl, setTagsMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [participantsMenuAnchorEl, setParticipantsMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [prioritiesMenuAnchorEl, setPrioritiesMenuAnchorEl] = useState<HTMLElement | null>(null);

  const handleUserDataClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserDataMenuAnchorEl(event.currentTarget);
  };

  const handleTagsClick = (event: React.MouseEvent<HTMLElement>) => {
    setTagsMenuAnchorEl(event.currentTarget);
  };

  const handleParticipantsClick = (event: React.MouseEvent<HTMLElement>) => {
    setParticipantsMenuAnchorEl(event.currentTarget);
  };

  const handlePrioritiesClick = (event: React.MouseEvent<HTMLElement>) => {
    setPrioritiesMenuAnchorEl(event.currentTarget);
  };

  const handleCloseUserData = () => {
    setUserDataMenuAnchorEl(null);
  };

  const handleCloseTags = () => {
    setTagsMenuAnchorEl(null);
  };

  const handleCloseParticipants = () => {
    setParticipantsMenuAnchorEl(null);
  };

  const handleClosePriorities = () => {
    setPrioritiesMenuAnchorEl(null);
  };

  const [activities, setActivities] = useState<Array<string | null>>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [sortedProcesses, setSortedProcesses] = useState<StartedProcess[]>([]);
  const [newSort, setNewSort] = useState<SortCriterion>({});
  const [sortField, setSortField] = useState<string | null>(null);
  const [loading, setLoading] = useState(false)

  const [showAllActivities, setShowAllActivities] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [showAllUserDataKeys, setShowAllUserDataKeys] = useState(false);
  const [showAllTags, setShowAllTags] = useState(false);
  const [showAllGroupsFilter, setShowAllGroupsFilter] = useState(false);
  const [showAllRolesFilter, setShowAllRolesFilter] = useState(false);
  const [showAllPriorities, setShowAllPriorities] = useState(false);
  const [showAllRoles, setShowAllRoles] = useState(false);
  const [pageData, setPageData] = useState<PageData>({
    users: { data: [], loaded: false },
    robots: { data: [], loaded: false },
    groups: { data: [], loaded: false },
    processes: { data: [], loaded: false },
    roles: { data: [], loaded: false },
    processModels: { data: [], loaded: false },
    userDataKeys: { data: [], loaded: false },
    tags: { data: [], loaded: false },
    priorities: { data: [], loaded: false },
    allGroups: { data: [], loaded: false },
    allRoles: { data: [], loaded: false },
  });

  const processes = useMemo(() => pageData.processes.data, [pageData]);
  const users = useMemo(() => pageData.users.data, [pageData]);
  const processModels = useMemo(() => pageData.processModels.data, [pageData]);
  const groups = useMemo(() => pageData.groups.data, [pageData]);
  const roles = useMemo(() => pageData.roles.data, [pageData]);
  const allUserDataKeys = useMemo(() => pageData.userDataKeys.data, [pageData]);
  const allTags = useMemo(() => pageData.tags.data, [pageData]);
  const allPriorities = useMemo(() => pageData.priorities.data, [pageData]);
  const allGroupsFilter = useMemo(() => pageData.allGroups.data, [pageData]);
  const allRolesFilter = useMemo(() => pageData.allRoles.data, [pageData]);

  const setUsers = (users: User[]) => {
    setPageData((prev) => ({ ...prev, users: { data: users, loaded: true } }));
  };
  const setGroups = (groups: Group[]) => {
    setPageData((prev) => ({
      ...prev,
      groups: { data: groups, loaded: true },
    }));
  };
  const setRoles = (roles: Role[]) => {
    setPageData((prev) => ({ ...prev, roles: { data: roles, loaded: true } }));
  };
  const setRobots = (robots: Robot[]) => {
    setPageData((prev) => ({
      ...prev,
      robots: { data: robots, loaded: true },
    }));
  };
  // FASE 1: arrivo sulla pagina
  // FASE 2: cambio organizzazione
  // FASE 3: esco dalla pagina

  // FASE 1: inizializzo filtri
  // FASE 2: modifico filtri

  const handleChangeSort = (key: string) => {
    setNewSort((_) => {
      switch (_[key]) {
        case undefined:
        case SortState.None:
          return { [key]: SortState.Desc };
        case SortState.Asc:
          return { [key]: SortState.None };
        case SortState.Desc:
          return { [key]: SortState.Asc };
      }
    });
  };

  const sortProcesses = useCallback(
    (processes: StartedProcess[]) => {
      if (newSort.activity) {
        return [...processes].sort((a, b) => {
          const nameA = a.flownodename?.toLowerCase();
          const nameB = b.flownodename?.toLowerCase();
          if (newSort.activity === SortState.Asc) {
            return (nameA || "").localeCompare(nameB || "");
          } else {
            return (nameB || "").localeCompare(nameA || "");
          }
        });
      }
      if (newSort.model) {
        return [...processes].sort((a, b) => {
          const nameA = a.process_description.toLowerCase();
          const nameB = b.process_description.toLowerCase();
          if (newSort.model === SortState.Asc) {
            return nameA.localeCompare(nameB);
          } else {
            return nameB.localeCompare(nameA);
          }
        });
      }
      if (newSort.processmodelname) {
        return [...processes].sort((a, b) => {
          const nameA = a.processmodelname.toLowerCase();
          const nameB = b.processmodelname.toLowerCase();
          if (newSort.processmodelname === SortState.Asc) {
            return nameA.localeCompare(nameB);
          } else {
            return nameB.localeCompare(nameA);
          }
        });
      }
      if (newSort.process) {
        return [...processes].sort((a, b) => {
          const dateA = a.process_startdate
            ? new Date(a.process_startdate).getTime()
            : 0;
          const dateB = b.process_startdate
            ? new Date(b.process_startdate).getTime()
            : 0;
          return newSort.process === SortState.Asc
            ? dateA - dateB
            : dateB - dateA;
        });
      }
      if (newSort.activityStart) {
        return [...processes].sort((a, b) => {
          const dateA = a.created ? new Date(a.created).getTime() : 0;
          const dateB = b.created ? new Date(b.created).getTime() : 0;
          return newSort.activityStart === SortState.Asc
            ? dateA - dateB
            : dateB - dateA;
        });
      }
      if (newSort.priorityorder) {
        return [...processes].sort((a, b) => {
          const valA = a.priorityorder ?? 0;
          const valB = b.priorityorder ?? 0;
          return newSort.priorityorder === SortState.Asc
            ? valA - valB
            : valB - valA;
        });
      }
      if (newSort.timeindoing) {
        return [...processes].sort((a, b) => {
          const valA = a.timeindoing ?? 0;
          const valB = b.timeindoing ?? 0;
          return newSort.timeindoing === SortState.Asc
            ? valA - valB
            : valB - valA;
        });
      }
      if (newSort.processduration) {
        return [...processes].sort((a, b) => {
          const valA = a.processduration ?? 0;
          const valB = b.processduration ?? 0;
          return newSort.processduration === SortState.Asc
            ? valA - valB
            : valB - valA;
        });
      }
      if (newSort.timetotakeover) {
        return [...processes].sort((a, b) => {
          const valA = a.timetotakeover ?? 0;
          const valB = b.timetotakeover ?? 0;
          return newSort.timetotakeover === SortState.Asc
            ? valA - valB
            : valB - valA;
        });
      }

      const sortedProcesses = [...processes];
      Object.keys(newSort).forEach((key) => {
        if (newSort[key] !== SortState.None) {
          const userDataSortType = checkUserDataSortType(
            sortedProcesses.map((a) => a.process_userdata),
            newSort[key],
            key
          );
          sortedProcesses.sort((a, b) => {
            const userDataA = getUserData(a.process_userdata);
            const userDataB = getUserData(b.process_userdata);
            const valueA = userDataA[key] ? userDataA[key].value : "";
            const valueB = userDataB[key] ? userDataB[key].value : "";
            return newSort[key] === SortState.Asc
              ? customSort(valueA, valueB, userDataSortType)
              : customSort(valueB, valueA, userDataSortType);
          });
        }
      });
      return sortedProcesses;
    },
    [newSort]
  );

  useEffect(() => {
    const filteredProcesses = processes.filter((process) => {
      const matchModels =
        (values.modelids?.length || 0) === 0 ||
        values.modelids.includes(process.process_idprocessmodel);
      const matchActivities =
        (values.activityids?.length || 0) === 0 ||
        values.activityids.includes(process.flownodename);
      const matchUserDataKeys =
        (values.userDataKeys?.length || 0) === 0 ||
        hasDuplicates([
          ...values.userDataKeys,
          ...Object.keys(getUserData(process.process_userdata)),
        ]);
      const matchUsers =
        (values.userids?.length || 0) === 0 ||
        values.userids.includes(String(process.iduser));
      const matchGroups =
        (values.groupids?.length || 0) === 0 ||
        users.some((user) => {
          const isUserMatch = user.id === process.iduser;
          if (isUserMatch) {
            const isGroupMatch = user.groups.some((group) =>
              values.groupids.includes(String(group.id))
            );
            return isGroupMatch;
          }
          return false;
        });

      const matchRoles =
        (values.roleids?.length || 0) === 0 ||
        users.some((user) => {
          const isUserMatch = user.id === process.iduser;

          if (isUserMatch) {
            const isRoleMatch = user.roles.some((role) =>
              values.roleids.includes(String(role.id))
            );
            return isRoleMatch;
          }
          return false;
        });

      const matchTags =
        (values.tags?.length || 0) === 0 ||
        (() => {
          try {
            const tags = typeof process.jsontag === 'string' ? JSON.parse(process.jsontag || '[]') : (process.jsontag || []);
            return Array.isArray(tags) && tags.some((t: any) => values.tags.includes(t.tag));
          } catch (e) {
            return false;
          }
        })();

      const matchGroupsFilter =
        (values.groups?.length || 0) === 0 ||
        (() => {
          try {
            const groups = typeof process.jsongroups === 'string' ? JSON.parse(process.jsongroups || '[]') : (process.jsongroups || []);
            return Array.isArray(groups) && groups.some((g: any) => values.groups.includes(g.groupname));
          } catch (e) {
            return false;
          }
        })();

      const matchRolesFilter =
        (values.roles?.length || 0) === 0 ||
        (() => {
          try {
            const roles = typeof process.jsonroles === 'string' ? JSON.parse(process.jsonroles || '[]') : (process.jsonroles || []);
            return Array.isArray(roles) && roles.some((r: any) => values.roles.includes(r.rolename));
          } catch (e) {
            return false;
          }
        })();

      const matchPriorities =
        (values.priorities?.length || 0) === 0 ||
        values.priorities.includes(process.priorityname);

      const matchSearch = values.search
        ? process.processmodelname
          .toLowerCase()
          .includes(values.search.toLowerCase()) ||
        process.flownodename
          ?.toLowerCase()
          .includes(values.search.toLowerCase()) ||
        process.process_description
          .toLowerCase()
          .includes(values.search.toLowerCase()) ||
        users.some(
          (user) =>
            user.id === process.iduser &&
            (user.firstname
              .toLowerCase()
              .includes(values.search.toLowerCase()) ||
              user.lastname
                .toLowerCase()
                .includes(values.search.toLowerCase()))
        ) ||
        Object.entries(getUserData(process.process_userdata)).some(
          ([key, value]) => {
            const _value = value.value;
            return (
              key.toLowerCase().includes(values.search.toLowerCase()) ||
              (typeof _value === "string"
                ? _value.toLowerCase().includes(values.search.toLowerCase())
                : String(_value).includes(values.search))
            );
          }
        )
        : true;

      const matchStatus =
        values.status === "current" ||
        (values.status === "doing" ? process.state === 1 : process.state === 0);
      return (
        matchModels &&
        matchActivities &&
        matchUsers &&
        matchSearch &&
        matchStatus &&
        matchUserDataKeys &&
        matchRoles &&
        matchGroups &&
        matchTags &&
        matchGroupsFilter &&
        matchRolesFilter &&
        matchPriorities
      );
    });

    const sortedProcesses = sortProcesses(filteredProcesses);
    setSortedProcesses(sortedProcesses);
  }, [processes, sortProcesses, values, users, newSort]);

  // fetch dei dati iniziali (processi, modelli e utenti) all'inizializzazione del componente
  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [{ data: processesData }, { data }] = await Promise.all([
        statisticsApi.get(`current/${organization.id}`),
        organizationsApi.get(`/${organization?.id}/processmodels/v2`),
      ]);
      const activeProcessModels = data.processmodels.filter((model: any) =>
        processesData.data.some(
          (process: StartedProcess) =>
            process.process_idprocessmodel === model.id
        )
      );
      setPageData((prev) => ({
        ...prev,
        processes: { data: processesData.data, loaded: true },
        processModels: { data: activeProcessModels, loaded: true },
        userDataKeys: {
          data: getUserDataKeysFromProcesses(processesData.data),
          loaded: true,
        },
        tags: {
          data: getUniqueTags(processesData.data),
          loaded: true,
        },
        priorities: {
          data: getUniqueValues(processesData.data, 'priorityname'),
          loaded: true,
        },
        allGroups: {
          data: getUniqueValues(processesData.data, 'jsongroups'),
          loaded: true,
        },
        allRoles: {
          data: getUniqueValues(processesData.data, 'jsonroles'),
          loaded: true,
        },
      }));
      return processesData.data as StartedProcess[];
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false)
    }
  }, [organization]);

  // fetch degli utenti dell'organizzazione
  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await organizationsApi.get(`${organization.id}/users`);
      if (Array.isArray(data.users)) {
        setUsers(data.users);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  }, [organization]);

  // useEffect(() => {
  //   // solo user che hanno modeli attivi
  //   const activeUserIds = new Set(
  //     processes
  //       .filter((process) => process.state === 1) // questi sono i processi in corso
  //       .map((process) => process.iduser) // e qua li mappiamo per utente
  //   );

  //   const activeUsers = users.filter((user) => activeUserIds.has(user.id));
  //   setFilteredUsers(activeUsers);
  // }, [users, processes]);

  useEffect(() => {
    setFilteredUsers(users);
  }, [users]);

  // fetch dei gruppi dell'organizzazione
  const fetchGroups = useCallback(async () => {
    try {
      const { data } = await organizationsApi.get(`${organization.id}/groups`);
      if (Array.isArray(data.groups)) {
        setGroups(data.groups);
      } else {
        setGroups([]);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
      setGroups([]);
    }
  }, [organization]);

  const fetchRoles = useCallback(async () => {
    try {
      const { data } = await organizationsApi.get(`${organization.id}/roles`);
      if (Array.isArray(data.roles)) {
        setRoles(data.roles);
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      setRoles([]);
    }
  }, [organization]);

  const fetchRobots = useCallback(async () => {
    try {
      const { data } = await organizationsApi.get(`${organization.id}/robots`);
      if (Array.isArray(data.robots)) {
        setRobots(data.robots);
      } else {
        setRobots([]);
      }
    } catch (error) {
      setRobots([]);
    }
  }, [organization]);

  useEffect(() => {
    setNewSort({});
  }, [sortField]);

  useEffect(() => {
    fetchUsers();
    fetchGroups();
    fetchRoles();
    fetchRobots();
  }, [fetchUsers, fetchGroups, fetchRoles, fetchRobots]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClearFilters = useCallback(
    (disableLocalStorage = false, clearUserData = false) => {
      setValues((prev: any) => ({
        status: "current",
        modelids: [],
        activityids: [],
        userids: [],
        groupids: [],
        roleids: [],
        search: "",
        userDataKeys: clearUserData ? [] : prev.userDataKeys,
        tags: [],
        groups: [],
        roles: [],
        priorities: [],
      }));
      if (!disableLocalStorage) {
        localStorage.removeItem(`${LOCAL_STORAGE_KEY}-${organization.id}`);
        localStorage.removeItem(`${SORT_STORAGE_KEY}-${organization.id}`);
        setNewSort({});
      }
    },
    [setValues, organization]
  );

  const handleClearUserdata = useCallback(() => {
    setValues((prev: any) => ({
      ...prev,
      userDataKeys: [],
    }));
  }, [setValues]);

  useEffect(() => {
    return () => handleClearFilters(true);
  }, [handleClearFilters]);

  useEffect(() => {
    return () => handleClearUserdata();
  }, [handleClearUserdata]);

  useEffect(() => {
    const value = doing && toDo ? "current" : doing ? "doing" : "todo";
    changeHandler("status", value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doing, toDo]);

  const extractUniqueActivities = (processes: StartedProcess[]) => {
    // qua le estraiamo (le attività uniché sennò mi vengono i doppioni sul filtro)
    return Array.from(
      new Set(
        processes.map((process) =>
          process.flownodetype === 0 && process.idtype === 3
            ? getTranslation(translations, "events.intermediate_catch")
            : process.flownodename
        )
      )
    );
  };

  useEffect(() => {
    setActivities(extractUniqueActivities(processes));
  }, [processes]);

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
  };

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleCheckboxChange = (category: string, value: string | null) => {
    const currentCategoryValues = values[category] || [];
    changeHandler(
      category,
      currentCategoryValues.includes(value)
        ? currentCategoryValues.filter((v: string) => v !== value)
        : [...currentCategoryValues, value]
    );
  };

  const activeFiltersCount = useMemo(() => {
    return (
      (values.modelids?.length || 0) +
      (values.activityids?.length || 0) +
      (values.roleids?.length || 0) +
      (values.userids?.length || 0) +
      (values.groupids?.length || 0) +
      (values.tags?.length || 0) +
      (values.groups?.length || 0) +
      (values.roles?.length || 0) +
      (values.priorities?.length || 0) +
      (values.search ? 1 : 0)
      // values.userDataKeys.length
    );
  }, [values]);

  const processFilterCount = useMemo(() => {
    return (
      (values.modelids?.length || 0) +
      (values.activityids?.length || 0)
    );
  }, [values]);

  const activeSortsCount = useMemo(() => {
    return Object.keys(newSort).filter((key) => newSort[key]).length;
  }, [newSort]);

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const savedFilters = localStorage.getItem(
      `${LOCAL_STORAGE_KEY}-${organization.id}`
    );
    if (savedFilters) {
      const parsed = JSON.parse(savedFilters);
      setValues((prev: any) => ({ ...prev, ...parsed }));
      if (parsed.status === "doing") {
        setDoing(true);
        setToDo(false);
      } else if (parsed.status === "todo") {
        setDoing(false);
        setToDo(true);
      } else {
        setDoing(true);
        setToDo(true);
      }
    }
    const savedSorts = localStorage.getItem(
      `${SORT_STORAGE_KEY}-${organization.id}`
    );
    if (savedSorts) {
      const parsedSorts = JSON.parse(savedSorts);
      setNewSort(parsedSorts);
    }
    setIsInitialized(true);
  }, [setValues, organization.id]);


  useEffect(() => {
    if (!isInitialized) return;

    localStorage.setItem(
      `${LOCAL_STORAGE_KEY}-${organization.id}`,
      JSON.stringify(values)
    );
  }, [values, organization, isInitialized]);

  useEffect(() => {
    if (!isInitialized) return;

    localStorage.setItem(
      `${SORT_STORAGE_KEY}-${organization.id}`,
      JSON.stringify(newSort)
    );
  }, [newSort, organization, isInitialized]);

  //anti-filter-breaker
  useEffect(() => {
    if (
      pageData.processModels.loaded &&
      pageData.groups.loaded &&
      pageData.roles.loaded &&
      pageData.processes.loaded &&
      pageData.userDataKeys.loaded
    ) {
      const processModels = pageData.processModels.data;
      const groups = pageData.groups.data;
      const roles = pageData.roles.data;
      const processes = pageData.processes.data;
      const allUserDataKeys = pageData.userDataKeys.data;
      const values = JSON.parse(
        localStorage.getItem(`${LOCAL_STORAGE_KEY}-${organization.id}`) || ""
      );
      if (values) {
        const validModelIds = new Set(processModels.map((model) => model.id));
        const validActivityIds = new Set(
          processes.map((process) => process.flownodename)
        );
        const validUserIds = new Set(
          processes.map((process) => String(process.iduser))
        );
        const validGroupIds = new Set(groups.map((group) => String(group.id)));
        const validRoleIds = new Set(roles.map((role) => String(role.id)));
        const validUserDataKeys = new Set(
          allUserDataKeys.map((key) => String(key))
        );
        const validTags = new Set(pageData.tags.data);
        const validGroupsFilter = new Set(pageData.allGroups.data);
        const validRolesFilter = new Set(pageData.allRoles.data);
        const validPriorities = new Set(pageData.priorities.data);

        const filteredModelIds = values.modelids.filter((id: number) =>
          validModelIds.has(id)
        );
        const filteredActivityIds = values.activityids.filter((id: string) =>
          validActivityIds.has(id)
        );
        const filteredUserIds = values.userids.filter((id: string) =>
          validUserIds.has(id)
        );
        const filteredGroupIds = values.groupids.filter((id: string) =>
          validGroupIds.has(id)
        );
        const filteredRoleIds = values.roleids.filter((id: string) =>
          validRoleIds.has(id)
        );
        const filteredUserDataKeys = values.userDataKeys.filter((key: string) =>
          validUserDataKeys.has(key)
        );
        const filteredTags = (values.tags || []).filter((tag: string) =>
          validTags.has(tag as any)
        );
        const filteredGroupsFilter = (values.groups || []).filter((group: string) =>
          validGroupsFilter.has(group)
        );
        const filteredRolesFilter = (values.roles || []).filter((role: string) =>
          validRolesFilter.has(role)
        );
        const filteredPriorities = (values.priorities || []).filter((priority: string) =>
          validPriorities.has(priority)
        );

        if (
          filteredModelIds.length !== values.modelids.length ||
          filteredActivityIds.length !== values.activityids.length ||
          filteredUserIds.length !== values.userids.length ||
          filteredGroupIds.length !== values.groupids.length ||
          filteredRoleIds.length !== values.roleids.length ||
          filteredUserDataKeys.length !== (values.userDataKeys || []).length ||
          filteredTags.length !== (values.tags || []).length ||
          filteredGroupsFilter.length !== (values.groups || []).length ||
          filteredRolesFilter.length !== (values.roles || []).length ||
          filteredPriorities.length !== (values.priorities || []).length
        ) {
          setValues({
            ...values,
            modelids: filteredModelIds,
            activityids: filteredActivityIds,
            userids: filteredUserIds,
            groupids: filteredGroupIds,
            roleids: filteredRoleIds,
            userDataKeys: filteredUserDataKeys,
            tags: filteredTags,
            groups: filteredGroupsFilter,
            roles: filteredRolesFilter,
            priorities: filteredPriorities,
          });
        }
      }
    }
  }, [pageData, setValues, organization.id]);

  return (
    <Stack height="100%" spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormGroup
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
              <MonitorSearchBar
                value={values.search}
                onChange={(newValue) => changeHandler("search", newValue)}
              />
              <FlowentiButton
                id="switch-doing"
                variant="outlined"
                startIcon={doing ? <CheckIcon /> : undefined}
                onClick={() => {
                  const nextDoingState = !doing;
                  if (!toDo && !nextDoingState) {
                    setToDo(true);
                  }
                  setDoing(nextDoingState);
                }}
              >
                {getTranslation(
                  translations,
                  "pm.label.process_status.doing"
                )}
              </FlowentiButton>
              <FlowentiButton
                sx={{ mr: 1 }}
                id="switch-todo"
                variant="outlined"
                startIcon={toDo ? <CheckIcon /> : undefined}
                onClick={() => {
                  const nextToDoState = !toDo;
                  if (!doing && !nextToDoState) {
                    setDoing(true);
                  }
                  setToDo(nextToDoState);
                }}
              >
                {getTranslation(
                  translations,
                  "pm.label.process_status.todo"
                )}
              </FlowentiButton>
              <Badge
                badgeContent={processFilterCount}
                color="primary"
                // sx={{ ml: 2 }}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <FlowentiButton
                  sx={{ mr: 1 }}
                  id="filter-menu"
                  onClick={handleFilterClick}
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                >
                  {getTranslation(translations, "pm.modelname.label.caption")}
                </FlowentiButton>
              </Badge>
              <Badge
                badgeContent={(values.groups?.length || 0) + (values.roles?.length || 0) + (values.userids?.length || 0)}
                color="primary"
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <FlowentiButton
                  sx={{ mr: 1 }}
                  id="participants-menu"
                  onClick={handleParticipantsClick}
                  variant="outlined"
                  startIcon={<PeopleIcon />}
                >
                  {getTranslation(translations, "generic.participants")}
                </FlowentiButton>
              </Badge>
              <Badge
                badgeContent={values.userDataKeys?.length}
                color="primary"
                // sx={{ ml: 2 }}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <FlowentiButton
                  sx={{ mr: 1 }}
                  id="userdata-menu"
                  onClick={handleUserDataClick}
                  variant="outlined"
                  startIcon={<FormatListBulletedIcon />}
                >
                  {getTranslation(translations, "generic.userdata.caption")}
                </FlowentiButton>
              </Badge>
              <Badge
                badgeContent={values.tags?.length}
                color="primary"
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <FlowentiButton
                  sx={{ mr: 1 }}
                  id="tags-menu"
                  onClick={handleTagsClick}
                  variant="outlined"
                  startIcon={<LabelOutlined />}
                >
                  {getTranslation(translations, "generic.labels.caption")}
                </FlowentiButton>
              </Badge>
              <Badge
                badgeContent={values.priorities?.length}
                color="primary"
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <FlowentiButton
                  sx={{ mr: 1 }}
                  id="priorities-menu"
                  onClick={handlePrioritiesClick}
                  variant="outlined"
                  startIcon={<PriorityHighIcon sx={{ mr: -1 }} />}
                >
                  {getTranslation(translations, "generic.priorities.caption")}
                </FlowentiButton>
              </Badge>
              <Badge
                badgeContent={activeSortsCount}
                color="primary"
                // sx={{ ml: 2 }}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <FlowentiButton
                  id="sort-menu"
                  onClick={handleSortClick}
                  variant="outlined"
                  // sx={{ ml: 2 }}
                  startIcon={<SortIcon />}
                >
                  {getTranslation(translations, "generic.sort.caption")}
                </FlowentiButton>
              </Badge>
            </Box>
          </FormGroup>
        </Grid>
      </Grid>
      <Menu
        anchorEl={userDataMenuAnchorEl}
        open={Boolean(userDataMenuAnchorEl)}
        onClose={handleCloseUserData}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box p={2} component={Stack}>
          <FormControl component="fieldset">
            {allUserDataKeys.length > 0 && (
              <Box display="flex" alignItems="center" justifyContent="space-around">
                <Typography component="legend" sx={{ fontWeight: "bold" }}>
                  {getTranslation(translations, "generic.userdata.caption")}
                </Typography>
                <IconButton color="primary" onClick={() => handleClearUserdata()}>
                  <FilterListOffIcon />
                </IconButton>
              </Box>
            )}

            <MuiFormGroup>
              {allUserDataKeys
                .slice(0, showAllUserDataKeys ? allUserDataKeys.length : 5)
                .map((key) => (
                  <Tooltip key={key} title={key}>
                    <FormControlLabel
                      slotProps={{
                        typography: {
                          sx: {
                            maxWidth: 150,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        },
                      }}
                      control={
                        <Checkbox
                          id={`userdata-filter-${key}`}
                          checked={values.userDataKeys?.includes(key)}
                          onChange={() =>
                            handleCheckboxChange("userDataKeys", key)
                          }
                          name={key}
                        />
                      }
                      label={key}
                    />
                  </Tooltip>
                ))}
              {allUserDataKeys.length > 5 && (
                <Button
                  onClick={() => setShowAllUserDataKeys(!showAllUserDataKeys)}
                  variant="text"
                  sx={{ mt: 1, mr: 22 }}
                >
                  {showAllUserDataKeys
                    ? getTranslation(translations, "generic.show.less")
                    : getTranslation(translations, "generic.show.all")}
                </Button>
              )}
            </MuiFormGroup>
          </FormControl>
        </Box>
      </Menu>
      <Menu
        anchorEl={tagsMenuAnchorEl}
        open={Boolean(tagsMenuAnchorEl)}
        onClose={handleCloseTags}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box p={2} component={Stack}>
          <FormControl component="fieldset">
            {allTags.length > 0 && (
              <Box display="flex" alignItems="center" justifyContent="space-around">
                <Typography component="legend" sx={{ fontWeight: "bold" }}>
                  {getTranslation(translations, "generic.labels.caption")}
                </Typography>
                <IconButton color="primary" onClick={() => changeHandler("tags", [])}>
                  <FilterListOffIcon />
                </IconButton>
              </Box>
            )}
            <MuiFormGroup>
              {allTags
                .slice(0, showAllTags ? allTags.length : 5)
                .map((tag) => (
                  <Tooltip key={tag.id} title={tag.name}>
                    <FormControlLabel
                      slotProps={{
                        typography: {
                          sx: {
                            maxWidth: 150,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        },
                      }}
                      control={
                        <Checkbox
                          id={`tags-filter-${tag.name}`}
                          checked={values.tags?.includes(tag.name)}
                          onChange={() =>
                            handleCheckboxChange("tags", tag.name)
                          }
                          name={tag.name}
                        />
                      }
                      label={
                        <Labels
                          labelCompleteMode
                          labels={[tag]}
                        />
                      }
                    />
                  </Tooltip>
                ))}
              {allTags.length > 5 && (
                <Button
                  onClick={() => setShowAllTags(!showAllTags)}
                  variant="text"
                  sx={{ mt: 1, mr: 22 }}
                >
                  {showAllTags
                    ? getTranslation(translations, "generic.show.less")
                    : getTranslation(translations, "generic.show.all")}
                </Button>
              )}
            </MuiFormGroup>
          </FormControl>
        </Box>
      </Menu>
      <Menu
        anchorEl={participantsMenuAnchorEl}
        open={Boolean(participantsMenuAnchorEl)}
        onClose={handleCloseParticipants}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box p={2} component={Stack} spacing={2}>
          <FormControl component="fieldset">
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography component="legend" sx={{ fontWeight: "bold" }}>
                {getTranslation(translations, "generic.participants")}
              </Typography>
              <IconButton size="small" color="primary" onClick={() => changeHandler("userids", [])}>
                <FilterListOffIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography component="legend" sx={{ fontWeight: "bold" }}>
                {getTranslation(translations, "generic.user")}
              </Typography>
            </Box>
            <MuiFormGroup>
              {filteredUsers
                .slice(0, showAllUsers ? filteredUsers.length : 5)
                .map((user) => (
                  <Tooltip
                    title={`${user.lastname} ${user.firstname}`}
                    key={user.id}
                  >
                    <FormControlLabel
                      slotProps={{
                        typography: {
                          sx: {
                            maxWidth: 180,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        },
                      }}
                      control={
                        <Checkbox
                          id={`users-filter-${user.id}`}
                          checked={values.userids.includes(String(user.id))}
                          onChange={() =>
                            handleCheckboxChange("userids", String(user.id))
                          }
                          name={`${user.lastname} ${user.firstname}`}
                        />
                      }
                      label={`${user.lastname} ${user.firstname}`}
                    />
                  </Tooltip>
                ))}
              {filteredUsers.length > 5 && (
                <Button
                  onClick={() => setShowAllUsers(!showAllUsers)}
                  variant="text"
                  size="small"
                  sx={{ mt: 0.5, alignSelf: 'flex-start' }}
                >
                  {showAllUsers
                    ? getTranslation(translations, "generic.show.less")
                    : getTranslation(translations, "generic.show.all")}
                </Button>
              )}
            </MuiFormGroup>
          </FormControl>
          {/* Groups Section */}
          <FormControl component="fieldset">
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography component="legend" sx={{ fontWeight: "bold" }}>
                {getTranslation(translations, "groups.name")}
              </Typography>
              {values.groups?.length > 0 && (
                <IconButton size="small" color="primary" onClick={() => changeHandler("groups", [])}>
                  <FilterListOffIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <MuiFormGroup>
              {allGroupsFilter
                .slice(0, showAllGroupsFilter ? allGroupsFilter.length : 5)
                .map((group) => (
                  <Tooltip key={group} title={group}>
                    <FormControlLabel
                      slotProps={{
                        typography: {
                          sx: {
                            maxWidth: 180,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        },
                      }}
                      control={
                        <Checkbox
                          id={`groups-filter-${group}`}
                          checked={values.groups?.includes(group)}
                          onChange={() =>
                            handleCheckboxChange("groups", group)
                          }
                          name={group}
                        />
                      }
                      label={group}
                    />
                  </Tooltip>
                ))}
              {allGroupsFilter.length > 5 && (
                <Button
                  onClick={() => setShowAllGroupsFilter(!showAllGroupsFilter)}
                  variant="text"
                  size="small"
                  sx={{ mt: 0.5, alignSelf: 'flex-start' }}
                >
                  {showAllGroupsFilter
                    ? getTranslation(translations, "generic.show.less")
                    : getTranslation(translations, "generic.show.all")}
                </Button>
              )}
            </MuiFormGroup>
          </FormControl>

          {/* Roles Section */}
          <FormControl component="fieldset">
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography component="legend" sx={{ fontWeight: "bold" }}>
                {getTranslation(translations, "roles.name")}
              </Typography>
              {values.roles?.length > 0 && (
                <IconButton size="small" color="primary" onClick={() => changeHandler("roles", [])}>
                  <FilterListOffIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <MuiFormGroup>
              {allRolesFilter
                .slice(0, showAllRolesFilter ? allRolesFilter.length : 5)
                .map((role) => (
                  <Tooltip key={role} title={role}>
                    <FormControlLabel
                      slotProps={{
                        typography: {
                          sx: {
                            maxWidth: 180,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        },
                      }}
                      control={
                        <Checkbox
                          id={`roles-filter-${role}`}
                          checked={values.roles?.includes(role)}
                          onChange={() =>
                            handleCheckboxChange("roles", role)
                          }
                          name={role}
                        />
                      }
                      label={role}
                    />
                  </Tooltip>
                ))}
              {allRolesFilter.length > 5 && (
                <Button
                  onClick={() => setShowAllRolesFilter(!showAllRolesFilter)}
                  variant="text"
                  size="small"
                  sx={{ mt: 0.5, alignSelf: 'flex-start' }}
                >
                  {showAllRolesFilter
                    ? getTranslation(translations, "generic.show.less")
                    : getTranslation(translations, "generic.show.all")}
                </Button>
              )}
            </MuiFormGroup>
          </FormControl>
        </Box>
      </Menu>
      <Menu
        anchorEl={prioritiesMenuAnchorEl}
        open={Boolean(prioritiesMenuAnchorEl)}
        onClose={handleClosePriorities}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box p={2} component={Stack}>
          <FormControl component="fieldset">
            {allPriorities.length > 0 && (
              <Box display="flex" alignItems="center" justifyContent="space-around">
                <Typography component="legend" sx={{ fontWeight: "bold" }}>
                  {getTranslation(translations, "generic.priorities.caption")}
                </Typography>
                <IconButton color="primary" onClick={() => changeHandler("priorities", [])}>
                  <FilterListOffIcon />
                </IconButton>
              </Box>
            )}
            <MuiFormGroup>
              {allPriorities
                .slice(0, showAllPriorities ? allPriorities.length : 5)
                .map((priority) => (
                  <Tooltip key={priority} title={priority}>
                    <FormControlLabel
                      slotProps={{
                        typography: {
                          sx: {
                            maxWidth: 150,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        },
                      }}
                      control={
                        <Checkbox
                          id={`priorities-filter-${priority}`}
                          checked={values.priorities?.includes(priority)}
                          onChange={() =>
                            handleCheckboxChange("priorities", priority)
                          }
                          name={priority}
                        />
                      }
                      label={priority}
                    />
                  </Tooltip>
                ))}
              {allPriorities.length > 5 && (
                <Button
                  onClick={() => setShowAllPriorities(!showAllPriorities)}
                  variant="text"
                  sx={{ mt: 1, mr: 22 }}
                >
                  {showAllPriorities
                    ? getTranslation(translations, "generic.show.less")
                    : getTranslation(translations, "generic.show.all")}
                </Button>
              )}
            </MuiFormGroup>
          </FormControl>
        </Box>
      </Menu>
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box component={Stack} p={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography component="legend" sx={{ fontWeight: "bold" }}>
              {getTranslation(translations, "pm.modelname.label.caption")}
            </Typography>
            <IconButton color="primary" onClick={() => handleClearFilters()}>
              <FilterListOffIcon />
            </IconButton>
          </Box>
          <Box minWidth={200}>
            {processModels.length > 0 && (
              <Typography
                variant="subtitle1"
                gutterBottom
                marginTop={2}
                fontWeight="bold"
              >
                {getTranslation(translations, "generic.filter.by.processmodel")}
              </Typography>
            )}
            <ProcessModelFilter
              processModelsId={values.modelids}
              onProcessModelsChange={(newModels) =>
                changeHandler("modelids", newModels)
              }
              options={processModels}
            />
          </Box>

          <FormControl component="fieldset" sx={{ marginTop: 2 }}>
            {activities.length > 0 && (
              <MuiFormGroup sx={{ fontWeight: "bold" }}>
                {getTranslation(
                  translations,
                  "pm.associations.assigned_activity"
                )}
                {activities
                  .slice(0, showAllActivities ? activities.length : 5)
                  .map((activity) => (
                    <Tooltip title={activity} key={activity}>
                      <FormControlLabel
                        slotProps={{
                          typography: {
                            sx: {
                              maxWidth: 150,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            },
                          },
                        }}
                        control={
                          <Checkbox
                            id={`activity-filter-${activity}`}
                            checked={values.activityids.includes(activity)}
                            onChange={() =>
                              handleCheckboxChange("activityids", activity)
                            }
                            name={activity || ""}
                          />
                        }
                        label={
                          activity ? activity : getTranslation(translations, "activity.no_name")
                        }
                      />
                    </Tooltip>
                  ))}
                {activities.length > 5 && (
                  <Button
                    onClick={() => setShowAllActivities(!showAllActivities)}
                    variant="text"
                    sx={{ mt: 1, mr: 22 }}
                  >
                    {showAllActivities
                      ? getTranslation(translations, "generic.show.less")
                      : getTranslation(translations, "generic.show.all")}
                  </Button>
                )}
              </MuiFormGroup>
            )}
          </FormControl>
        </Box>
      </Menu>
      <Menu
        id="sort-menu"
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={handleSortClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box p={2} sx={{ minWidth: 250 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography sx={{ fontWeight: "bold" }}>
              {getTranslation(translations, "generic.sort.caption")}
            </Typography>
            {activeSortsCount > 0 && (
              <IconButton size="small" color="primary" onClick={() => setNewSort({})}>
                <FilterListOffIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Stack spacing={1}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body2">
                {getTranslation(translations, "generic.filter.data.start.process")}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleChangeSort("process")}
                sx={{ opacity: newSort.process ? 1 : 0.3 }}
              >
                {newSort.process === SortState.Asc ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <ArrowDownwardIcon fontSize="small" />
                )}
              </IconButton>
            </Box>

            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body2">
                {getTranslation(translations, "generic.filter.data.start.activity")}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleChangeSort("activityStart")}
                sx={{ opacity: newSort.activityStart ? 1 : 0.3 }}
              >
                {newSort.activityStart === SortState.Asc ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <ArrowDownwardIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
          </Stack>
        </Box>
      </Menu>
      {loading ?
        <CircularProgress sx={{ alignSelf: "center" }} />
        :
        <ProcessesList
          handleChangeSort={handleChangeSort}
          newSort={newSort}
          refresh={fetchData}
          title={getTranslation(translations, "generic.processes.monitor")}
          processes={sortedProcesses}
          filters={values}
          filteredUserDataKeys={values.userDataKeys}
        />
      }
    </Stack>
  );
};

export default ProcessStarted;
